import { AddIcon, ArrowForwardIcon } from "@chakra-ui/icons";
import { Container, Card, CardHeader, Heading, Button, CardBody, Text, Divider, Box, Flex, Spacer, Badge, Input, Stack, HStack, FormControl, FormLabel, IconButton } from "@chakra-ui/react";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { useAppDispatch } from "store/store.hook";
import { FaChartBar, FaList, FaListUl } from "react-icons/fa6";
import { FaEdit } from "react-icons/fa";
import MyBreadcrumb from "components/breadcrumb/Breadcrumb";
import { Breadcrumb } from "types/Breadcrumb";


/**
 * TargetDashboard component renders the dashboard for a specific target.
 * It displays the target's title, description, and provides navigation buttons
 * to view, edit, and visualize charts related to the target.
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @component
 *
 * @example
 * // Usage example:
 * <TargetDashboard />
 *
 * @remarks
 * This component uses several hooks and components from the Redux and React Router libraries:
 * - `useAppDispatch` to dispatch actions to the Redux store.
 * - `useParams` to access route parameters.
 * - `useSelector` to select data from the Redux store.
 *
 * The component also uses Chakra UI components for styling and layout:
 * - `Container`, `Card`, `CardHeader`, `CardBody`, `Heading`, `Text`, `Divider`, `Flex`, `Box`, `Button`, `Spacer`
 *
 * Additionally, it uses custom components and icons:
 * - `MyBreadcrumb` for displaying breadcrumbs.
 * - `FaListUl`, `FaEdit`, `FaChartBar`, `ArrowForwardIcon` for icons.
 *
 * @requires
 * - `useAppDispatch` from `hooks`
 * - `useParams` from `react-router-dom`
 * - `useSelector` from `react-redux`
 * - `Breadcrumb` from `components/Breadcrumb`
 * - `MyBreadcrumb` from `components/MyBreadcrumb`
 * - `FaListUl`, `FaEdit`, `FaChartBar`, `ArrowForwardIcon` from `react-icons/fa`
 */

export default function TargetDashoboard() {

  const dispatch = useAppDispatch();
  const params = useParams();
  const { targetIndex } = params;
  const setSelectedDocumentId = useSelector((state: any) => state.documents.selectedDocumentId);
  const selectedDocument = (useSelector((state: any) => state.documents.documents.find((doc: any) => doc._id === setSelectedDocumentId)));
  const target = selectedDocument.targets.find((target: any) => target.index === targetIndex);
  const isActive = selectedDocument.isActive;

  const breadcrumb: Breadcrumb = new Breadcrumb(
    [
      { label: "Obiettivi", link: "/targets", value: "", isActive: true },
      { label: `Obiettivo ${targetIndex}`, link: `/targets/${targetIndex}`, value: "", isActive: true },
    ]
  );

  return (


    <section className="dashboard-first-section">
      <Container maxW="container.lg" maxH={"container.xl"} textAlign={"left"}>
        <MyBreadcrumb breadcrumb={breadcrumb} />


        <Card minH={"60vh"}>
          <CardHeader>
            <Heading as="h1" size="xl" >
              Obiettivo {targetIndex} - {target?.title}
            </Heading>
            <Text>
              {target?.description}
            </Text>
            <Divider />
          </CardHeader>
          <CardBody>

            <Flex flexDirection={"column"}>
              <Box >
                <Link to={`/targets/${targetIndex}/view`}>
                  <Button width="full" colorScheme="blue" variant="outline"
                    _hover={{
                      bg: "blue.100",
                    }}
                  >
                    <Flex alignItems="center" justifyContent="space-between" width="100%">
                      <FaListUl />
                      <Divider orientation='vertical' height={"24px"} mx={2} />
                      <Text>Visualizza dati</Text>
                      <Spacer />
                      <ArrowForwardIcon />
                    </Flex>
                  </Button>
                </Link>
              </Box>
              <Box my={5}>
                <Link to={`/targets/${targetIndex}/edit`}>
                  <Button width={"full"} colorScheme='yellow' variant='outline'
                    _hover={{
                      bg: "yellow.100",
                    }}
                    isDisabled={!isActive}
                  >

                    <Flex alignItems="center" justifyContent="space-between" width="100%">
                      <FaEdit />
                      <Divider orientation='vertical' height={"24px"} mx={2} />
                      <Text>Modifica dati
                      </Text>
                      <Spacer />
                      <ArrowForwardIcon />
                    </Flex>
                  </Button>
                </Link>
              </Box>
              <Box>
                <Link to={`/targets/${targetIndex}/charts`}>
                  <Button width={"full"} colorScheme='green' variant='outline'
                    _hover={{
                      bg: "green.100",
                    }}>
                    <Flex alignItems="center" justifyContent="space-between" width="100%">
                      <FaChartBar />
                      <Divider orientation='vertical' height={"24px"} mx={2} />
                      <Text>Visualizza grafici </Text>
                      <Spacer />
                      <ArrowForwardIcon />
                    </Flex>
                  </Button>
                </Link>
              </Box>
            </Flex>

          </CardBody>
        </Card>
      </Container>
    </section>

  )

}
